import {
  Component,
  ContentChild,
  Directive,
  Input,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { animate, style, transition, trigger } from '@angular/animations';
import { TranslateModule } from '@ngx-translate/core';

/** Default number of rows a sidebar list shows before "Show more". */
export const SIDEBAR_LIST_LIMIT = 5;

/** Context available to a row template: `let-item let-isLast="last" let-i="index"`. */
export interface ShowMoreItemContext<T> {
  $implicit: T;
  index: number;
  /** True for the last row currently rendered (the last visible one while collapsed). */
  last: boolean;
}

/**
 * Marks the row template of an `app-show-more-list` and carries its items,
 * which keeps the row context strongly typed under strict templates.
 *
 * Bug 2857: sidebar lists are limited to 5 items with a "Show more" option.
 */
@Directive({ selector: 'ng-template[appShowMoreItemOf]' })
export class ShowMoreItemDirective<T> {
  @Input({ required: true }) appShowMoreItemOf: readonly T[] | null | undefined;

  constructor(readonly template: TemplateRef<ShowMoreItemContext<T>>) {}

  static ngTemplateContextGuard<T>(
    _dir: ShowMoreItemDirective<T>,
    ctx: unknown,
  ): ctx is ShowMoreItemContext<T> {
    return true;
  }
}

/**
 * Renders the first {@link limit} rows of a sidebar list, followed by a
 * "Show more (N)" / "Show less" toggle that expands the remaining rows with
 * a collapse/expand animation. Rows are laid out by the parent list wrapper
 * (the host uses `display: contents`).
 *
 * Bug 2857: sidebar lists are limited to 5 items with a "Show more" option.
 */
@Component({
  selector: 'app-show-more-list',
  templateUrl: './show-more-list.component.html',
  styleUrls: ['./show-more-list.component.scss'],
  imports: [NgTemplateOutlet, TranslateModule],
  animations: [
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: '0', opacity: '0', transform: 'translateY(-10px)' }),
        animate(
          '300ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ height: '*', opacity: '1', transform: 'translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        animate(
          '250ms cubic-bezier(0.4, 0.0, 0.2, 1)',
          style({ height: '0', opacity: '0', transform: 'translateY(-10px)' }),
        ),
      ]),
    ]),
  ],
})
export class ShowMoreListComponent<T> {
  @Input() limit = SIDEBAR_LIST_LIMIT;
  @ContentChild(ShowMoreItemDirective, { static: true })
  item!: ShowMoreItemDirective<T>;

  expanded = false;

  private splitFor?: readonly T[] | null;
  private splitLimit?: number;
  private headItems: readonly T[] = [];
  private tailItems: readonly T[] = [];

  /** Rows always shown. */
  get head(): readonly T[] {
    this.split();
    return this.headItems;
  }

  /** Rows shown only while expanded. */
  get tail(): readonly T[] {
    this.split();
    return this.tailItems;
  }

  toggle(): void {
    this.expanded = !this.expanded;
  }

  /** Re-slices only when the items array or the limit changes. */
  private split(): void {
    const items = this.item.appShowMoreItemOf;
    if (items === this.splitFor && this.limit === this.splitLimit) {
      return;
    }
    this.splitFor = items;
    this.splitLimit = this.limit;
    this.headItems = items?.slice(0, this.limit) ?? [];
    this.tailItems = items?.slice(this.limit) ?? [];
  }
}
